import type { Display } from "wylo-rc-session";

export type TileFrame = { left: string; top: string; width: string; height: string };

export type PxBox = { x: number; y: number; width: number; height: number };

type Ratios = { left: number; top: number; width: number; height: number };

function tileRatios(display: Display, displays: readonly Display[]): Ratios {
  const minX = Math.min(...displays.map((item) => item.bounds.x));
  const minY = Math.min(...displays.map((item) => item.bounds.y));
  const maxX = Math.max(...displays.map((item) => item.bounds.x + item.bounds.width));
  const maxY = Math.max(...displays.map((item) => item.bounds.y + item.bounds.height));
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  return {
    left: (display.bounds.x - minX) / width,
    top: (display.bounds.y - minY) / height,
    width: display.bounds.width / width,
    height: display.bounds.height / height,
  };
}

export function tileFrame(display: Display, displays: readonly Display[]): TileFrame {
  const ratios = tileRatios(display, displays);
  return {
    left: `${ratios.left * 100}%`,
    top: `${ratios.top * 100}%`,
    width: `${ratios.width * 100}%`,
    height: `${ratios.height * 100}%`,
  };
}

export function containBox(viewport: PxBox, frameWidth: number, frameHeight: number): PxBox | null {
  if (viewport.width <= 0 || viewport.height <= 0 || frameWidth <= 0 || frameHeight <= 0) return null;
  const scale = Math.min(viewport.width / frameWidth, viewport.height / frameHeight);
  const width = frameWidth * scale;
  const height = frameHeight * scale;
  return {
    x: viewport.x + (viewport.width - width) / 2,
    y: viewport.y + (viewport.height - height) / 2,
    width,
    height,
  };
}

export function displayViewport(
  view: "mosaic" | "single",
  stage: { width: number; height: number },
  display: Display,
  displays: readonly Display[],
): PxBox {
  if (view === "single") return { x: 0, y: 0, width: stage.width, height: stage.height };
  const ratios = tileRatios(display, displays);
  return {
    x: ratios.left * stage.width,
    y: ratios.top * stage.height,
    width: ratios.width * stage.width,
    height: ratios.height * stage.height,
  };
}

export function paintedFrame(
  view: "mosaic" | "single",
  stage: { width: number; height: number },
  display: Display,
  displays: readonly Display[],
  source?: { width: number; height: number } | null,
): PxBox | null {
  const width = source && source.width > 0 ? source.width : display.bounds.width;
  const height = source && source.height > 0 ? source.height : display.bounds.height;
  return containBox(displayViewport(view, stage, display, displays), width, height);
}

function unit(value: number): number | null {
  if (value < -1e-6 || value > 1 + 1e-6) return null;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

export function pointerOnDisplay(
  view: "mosaic" | "single",
  stage: { width: number; height: number },
  display: Display,
  displays: readonly Display[],
  local: { x: number; y: number },
  source?: { width: number; height: number } | null,
  clamp = false,
): { x: number; y: number } | null {
  const painted = paintedFrame(view, stage, display, displays, source);
  if (!painted || painted.width === 0 || painted.height === 0) return null;
  const rawX = (local.x - painted.x) / painted.width;
  const rawY = (local.y - painted.y) / painted.height;
  if (clamp) return { x: clampUnit(rawX), y: clampUnit(rawY) };
  const x = unit(rawX);
  const y = unit(rawY);
  if (x === null || y === null) return null;
  return { x, y };
}
